import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FormDisplayComponent } from '@modules/forms/components/form-display/form-display.component';
import { LottieLabelComponent } from '@modules/shared/lottie-label/lottie-label.component';
import { PAGINATED_LIST_SOURCE, PaginatedListComponent } from '@modules/shared/paginated-list/paginated-list.component';
import { CreatorType } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { computeAlerts, getReport } from '@pixacare/forms';
import { DialogService } from '@services/dialog.service';
import { SequenceHttpService } from '@services/http/sequence.http.service';
import { TelemonitoringHttpService } from '@services/http/telemonitoring.http.service';
import { BasePagination } from '@shared/base-pagination';
import { SequencePreviewMode } from '@shared/models/enums/sequence-preview-mode.enum';
import { TuiButton, TuiHint, TuiLink } from '@taiga-ui/core';
import { TuiChip } from '@taiga-ui/kit';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { SequenceContext } from 'src/app/shared/models/enums/sequence-context.enum';
import { MediaClickedOutput } from 'src/app/shared/models/media-clicked-output';
import { MenuAction } from 'src/app/shared/models/menu-actions/menu-action';
import { TelemonitoringLinkDirective } from '../../telemonitoring/directives/telemonitoring-link.directive';
import { SequenceSadmLinkDirective } from '../directives/sequence-sadm-link.directive';
import { SequenceTelemonitoringLinkDirective } from '../directives/sequence-telemonitoring-link.directive';
import { SequenceDescriptionDialogComponent } from '../sequence-description-dialog/sequence-description-dialog.component';
import { SequencePreviewTileComponent } from '../sequence-preview/sequence-preview-tile.component';
import { SequencePreviewComponent } from '../sequence-preview/sequence-preview.component';

@Component({
  selector: 'pxc-sequence-list',
  templateUrl: './sequence-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SequencePreviewComponent,
    SequenceSadmLinkDirective,
    SequenceTelemonitoringLinkDirective,
    TuiHint,
    TuiLink,
    TuiChip,
    TuiButton,
    PaginatedListComponent,
    TranslocoDirective,
    LottieLabelComponent,
    SequenceDescriptionDialogComponent,
    SequencePreviewTileComponent,
    TelemonitoringLinkDirective,
  ],
  host: {
    // The scroll tracker of the paginated list adds 2px on top: 6px + 2px = the 8px gap under the filter bar.
    class: 'block h-full pb-2 pt-1.5',
  },
})
export class SequenceListComponent implements OnChanges {
  @Input() sequences: FlattenedSequence[] = [];
  @Input() actions: MenuAction<FlattenedSequence>[] = null;
  @Input() context: SequenceContext = SequenceContext.SEQUENCE;
  @Input() showPatient = true;
  @Input() showActions = true;
  @Input() isPatientClickable = true;
  @Input() mode: SequencePreviewMode = SequencePreviewMode.ROW;

  @Output() sequenceMediaClicked = new EventEmitter<MediaClickedOutput>();

  SequenceContext = SequenceContext;
  CreatorType = CreatorType;
  SequencePreviewMode = SequencePreviewMode;

  private readonly translocoService = inject(TranslocoService);
  private readonly dialogService = inject(DialogService);
  private readonly sequenceService = inject(SequenceHttpService);
  private readonly telemonitoringService = inject(TelemonitoringHttpService);
  private readonly pagination: BasePagination<FlattenedSequence> = inject(PAGINATED_LIST_SOURCE, { optional: true });

  sequencesCollection$ = this.pagination
    ? this.pagination.select({}).pipe(map((collection) => collection.data))
    : of(this.sequences);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.sequences && !this.pagination) {
      this.sequencesCollection$ = of(this.sequences);
    }
  }

  openPatientFormAnswer(sequence: FlattenedSequence): void {
    combineLatest([
      this.telemonitoringService.getPatientForm(sequence.sequenceInstance.telemonitoringId),
      this.sequenceService.getPatientFormAnswer({
        clientCode: sequence.clientCode,
        sequenceId: sequence.sequenceInstance.id,
      }),
    ])
      .pipe(
        map(([patientForm, formAnswers]) => ({
          sections: getReport(patientForm, formAnswers).sections,
          alerts: computeAlerts(formAnswers, patientForm),
        })),
        switchMap((data) =>
          this.dialogService.openComponent(FormDisplayComponent, {
            label: this.translocoService.translate('Questionnaire patient'),
            size: 'm',
            data,
          }),
        ),
      )
      .subscribe();
  }
}
